<template>
    <single-article :article="article" :breadcrumbs="breadcrumbs" />
</template>

<script>
import { StatusCodes } from 'http-status-codes';

import { ArticleType } from '@/enums/articles';
import SingleArticle from '@/components/articles/SingleArticle';

export default {
    components: {
        SingleArticle
    },

    async asyncData({
        error,
        query,
        store: { dispatch },
        params: { slug = null }
    }) {
        try {
            const params = {
                preview: query.preview,
                uuid: query.uuid
            };

            const article = await dispatch('articles/show', {
                id: slug,
                params
            });

            const category = [...article.categories].pop();

            const clinicalResearchCategorySlug = query.clinicalResearch;

            let subcategory = {};
            let parentCategory = {};
            let mainCategory = {};

            if (category?.subcategories) {
                subcategory = [...category.subcategories].pop();
            } else if (category?.parentCategory) {
                parentCategory = category.parentCategory;

                if (parentCategory?.parentCategory) {
                    mainCategory = parentCategory.parentCategory;
                }
            }

            return {
                article,
                category,
                subcategory,
                mainCategory,
                parentCategory,
                clinicalResearchCategorySlug
            };
        } catch (err) {
            if (err.response?.status !== StatusCodes.INTERNAL_SERVER_ERROR) {
                return error({ statusCode: err.response.status });
            }

            console.error(err);

            return error({ statusCode: StatusCodes.INTERNAL_SERVER_ERROR });
        }
    },

    computed: {
        breadcrumbs() {
            return [
                {
                    text: this.$t('homepage.home'),
                    to: this.localePath('/')
                },
                ...this.middleBreadcrumbs,
                {
                    text: this.article.name,
                    active: true
                }
            ];
        },

        middleBreadcrumbs() {
            if (this.article.type === ArticleType.BRANCH_ACTIVITIES) {
                return [
                    {
                        text: this.$t('branchActivities.pageTitle'),
                        to: this.localePath(`/branch-activities`)
                    }
                ];
            }

            if (this.article.type === ArticleType.SPEAKER_MESSAGE) {
                return [
                    {
                        text: this.$t('speakerMessages.pageTitle'),
                        to: this.localePath(`/speaker-messages`)
                    }
                ];
            }

            if (this.article.type === ArticleType.CLINICAL_RESEARCH) {
                const middleBreadcrumbs = [
                    {
                        text: this.$t('clinicalResearches.pageTitle'),
                        to: this.localePath('/clinical-researches')
                    }
                ];

                let itemToAdd = null;

                if (this.clinicalResearchCategorySlug) {
                    itemToAdd = this.article.clinicalResearchLists.find(
                        clinicalResearchList =>
                            clinicalResearchList.category.slug ===
                            this.clinicalResearchCategorySlug
                    );
                } else if (this.article.clinicalResearchLists.length) {
                    itemToAdd = this.article.clinicalResearchLists[0];
                }

                if (itemToAdd) {
                    const {
                        category: { title, slug }
                    } = itemToAdd;

                    middleBreadcrumbs.push({
                        text: title,
                        to: this.localePath(
                            `/clinical-researches/categories/${slug}`
                        )
                    });
                }

                return middleBreadcrumbs;
            }

            if (this.mainCategory?.slug) {
                const middleBreadcrumbs = [];

                middleBreadcrumbs.push({
                    text: this.mainCategory.name,
                    to: this.localePath(`/categories/${this.mainCategory.slug}`)
                });

                middleBreadcrumbs.push({
                    text: this.parentCategory.name,
                    to: this.localePath(
                        `/subcategories/${this.parentCategory.slug}`
                    )
                });

                middleBreadcrumbs.push({
                    text: this.category.name,
                    to: this.localePath(`/subcategories/${this.category.slug}`)
                });

                return middleBreadcrumbs;
            }

            if (this.article.categories.length) {
                let middleBreadcrumbs = [];

                if (this.parentCategory.id) {
                    middleBreadcrumbs = [
                        {
                            text: this.parentCategory.name,
                            to: this.localePath(
                                `/categories/${this.parentCategory.slug}`
                            )
                        }
                    ];

                    middleBreadcrumbs.push({
                        text: this.category.name,
                        to: this.localePath(
                            `/subcategories/${this.category.slug}`
                        )
                    });
                } else {
                    middleBreadcrumbs = [
                        {
                            text: this.category.name,
                            to: this.localePath(
                                `/categories/${this.category.slug}`
                            )
                        }
                    ];
                }

                if (this.subcategory?.slug) {
                    middleBreadcrumbs.push({
                        text: this.subcategory.name,
                        to: this.localePath(
                            `/subcategories/${this.subcategory.slug}`
                        )
                    });
                }

                return middleBreadcrumbs;
            }

            if (this.article.contents.length) {
                const middleBreadcrumbs = [];

                const [page] = this.article.contents;

                if (page.categories.length) {
                    const [category] = page.categories;
                    const type = category.parentCategoryId
                        ? 'subcategories'
                        : 'categories';

                    middleBreadcrumbs.push({
                        text: category.name,
                        to: this.localePath(`/${type}/${category.slug}`)
                    });
                }

                middleBreadcrumbs.push({
                    text: page.name,
                    to: this.localePath(`/${page.url || ''}`)
                });

                return middleBreadcrumbs;
            }

            return [];
        }
    }
};
</script>
